import { useMemo, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { db } from '../datos/db.js'
import {
  cuentaDelCliente,
  type CargoDePaciente,
} from '../datos/consultas.js'
import { diaLocal, fechaCorta, guaranies, hoy } from '../datos/formato.js'
import { FormularioPago } from '../comunes/FormulariosCobro.js'
import { Boton } from '../comunes/Boton.js'

/**
 * La solapa «Cuenta» del paciente (T23).
 *
 * El dinero vive por paciente —procedimientos, pagos, saldo—, no sólo como
 * lista del día. Acá se lee la cuenta entera, sin salir del paciente: lo que
 * debe, lo que pagó y de qué cita salió cada cargo. Es el caso real del
 * mostrador: la persona vino a pagar una deuda vieja, no a que la atiendan.
 *
 * **Sólo Dexie.** Es pantalla de mostrador y tiene que funcionar sin red. El
 * pago se escribe como ya se escribe en `FormularioPago` (importado, no
 * copiado): `db.pago.add` + `encolar`. Nada de endpoints nuevos acá.
 *
 * Una sola fila abierta a la vez, como en `Cobros`: el formulario aparece al
 * abrir la fila con saldo, y el resto de la lista sigue siendo una lectura
 * rápida de la deuda.
 */
export function Cuenta({
  clienteId,
  onCobrar,
}: {
  clienteId: string
  onCobrar?: ((citaId: string) => void) | undefined
}) {
  /** La única fila abierta. `null` = todas cerradas. */
  const [abierta, setAbierta] = useState<string | null>(null)

  const cuenta = useLiveQuery(() => cuentaDelCliente(clienteId), [clienteId], [])
  const servicios = useLiveQuery(() => db.servicio.toArray(), [], [])
  const cliente = useLiveQuery(() => db.cliente.get(clienteId), [clienteId], null)

  const serviciosPorId = useMemo(() => new Map(servicios.map((s) => [s.id, s])), [servicios])

  // «Según la cobertura del cliente»: si tiene seguro, el pago sugiere el
  // tercero desde el arranque, porque a la obra social es a quien se le reclama.
  const sugerirTercero = (cliente?.cobertura_id ?? null) !== null

  const totalCargado = cuenta.reduce((a, c) => a + c.cargo.monto, 0)
  const cobrado = cuenta.reduce((a, c) => a + c.pagado, 0)
  const saldo = cuenta.reduce((a, c) => a + c.saldo, 0)

  // Con saldo primero; dentro de cada grupo, la cuenta ya viene del más nuevo
  // al más viejo.
  const ordenada = [...cuenta.filter((c) => c.saldo > 0), ...cuenta.filter((c) => c.saldo === 0)]

  const alternar = (cargoId: string) =>
    setAbierta((actual) => (actual === cargoId ? null : cargoId))

  return (
    <div className="max-w-2xl">
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Total etiqueta="Total cargado" monto={totalCargado} />
        <Total etiqueta="Cobrado" monto={cobrado} />
        <Total etiqueta="Saldo" monto={saldo} alerta={saldo > 0} />
      </div>

      {cuenta.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white py-12 text-center">
          <p className="txt-body">Todavía no se le cargó nada.</p>
          <p className="txt-meta mt-1">
            Los servicios aparecen acá cuando se registra el cargo de una cita.
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          {ordenada.map((item) => (
            <FilaCargo
              key={item.cargo.id}
              item={item}
              nombreServicio={serviciosPorId.get(item.cargo.servicio_id)?.nombre ?? 'Servicio'}
              abierta={abierta === item.cargo.id}
              onAlternar={() => alternar(item.cargo.id)}
              onCobrar={onCobrar}
              onCobrableHoy={item.cuando !== null && diaLocal(item.cuando) === hoy()}
              sugerirTercero={sugerirTercero}
            />
          ))}
        </ul>
      )}
    </div>
  )
}

function Total({ etiqueta, monto, alerta }: { etiqueta: string; monto: number; alerta?: boolean }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <p className="txt-meta uppercase tracking-wide">{etiqueta}</p>
      <p
        className={`mt-1 text-[22px] font-medium tabular-nums ${
          alerta ? 'text-amber-700' : monto > 0 ? 'text-slate-900' : 'text-slate-400'
        }`}
      >
        {guaranies(monto)}
      </p>
    </div>
  )
}

function FilaCargo({
  item,
  nombreServicio,
  abierta,
  onAlternar,
  onCobrar,
  onCobrableHoy,
  sugerirTercero,
}: {
  item: CargoDePaciente
  nombreServicio: string
  abierta: boolean
  onAlternar: () => void
  onCobrar?: ((citaId: string) => void) | undefined
  /** El cargo es de una cita del día: desde acá se puede saltar a «Cobros». */
  onCobrableHoy: boolean
  sugerirTercero: boolean
}) {
  const conSaldo = item.saldo > 0
  const fecha = item.cuando ? fechaCorta(item.cuando) : 'sin fecha'

  const contenido = (
    <>
      <span className="w-24 shrink-0 text-[13px] tabular-nums text-slate-500">{fecha}</span>
      <span className="min-w-0 flex-1 truncate text-[14px] text-slate-800">{nombreServicio}</span>
      <span className="shrink-0 text-right text-[13px] tabular-nums text-slate-600">
        {guaranies(item.cargo.monto)}
      </span>
      <span className="shrink-0 text-right text-[13px] tabular-nums text-slate-500">
        pagado {guaranies(item.pagado)}
      </span>
      <span
        className={`w-24 shrink-0 text-right text-[13px] tabular-nums ${
          conSaldo ? 'font-medium text-amber-700' : 'text-slate-500'
        }`}
      >
        {guaranies(item.saldo)}
      </span>
    </>
  )

  // Lo saldado no se abre: no hay nada que cobrar. Es una lectura, y por eso
  // tampoco compite con la fila abierta.
  if (!conSaldo) {
    return (
      <li className="flex flex-wrap items-baseline gap-x-3 rounded-xl border border-slate-200 bg-white px-4 py-3 opacity-70">
        {contenido}
      </li>
    )
  }

  return (
    <li className="overflow-hidden rounded-xl border border-amber-200 bg-white">
      <button
        type="button"
        onClick={onAlternar}
        aria-expanded={abierta}
        className="flex w-full flex-wrap items-baseline gap-x-3 px-4 py-3 text-left transition hover:bg-amber-50/50"
      >
        {contenido}
      </button>
      {abierta && (
        <div className="border-t border-amber-100 bg-amber-50/40 px-4 py-3">
          <FormularioPago cargoId={item.cargo.id} saldo={item.saldo} sugerirTercero={sugerirTercero} />
          {onCobrar && onCobrableHoy && (
            <div className="mt-3 border-t border-amber-200/60 pt-2">
              <Boton tamano="sm" variante="ghost" onClick={() => onCobrar(item.citaId)}>
                Ver en cobros
              </Boton>
            </div>
          )}
        </div>
      )}
    </li>
  )
}